import { Box, Tooltip, Typography } from '@mui/material';
import { TIME_FILTER_TABS, TimeFilterTabs } from '@/utilities/etransportTypes';
import { useTranslation } from 'react-i18next';
import { SearchComponent } from '@/components/common/Search';
import InfoIcon from '@mui/icons-material/Info';
import { CategoryCountSelector } from '@/components/common/selection/CategoryCountSelector';
import './etransport.css';

export function FilterBar({
  isLoading,
  activeTab,
  categories,
  searchTarget,
  setActiveTab,
  onSearchChange,
}: {
  isLoading: boolean;
  activeTab: TimeFilterTabs;
  categories: Map<TimeFilterTabs, number>;
  searchTarget: string | undefined;
  setActiveTab: (tab: TimeFilterTabs) => void;
  onSearchChange: (value: string | undefined) => void;
}) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        alignItems: 'flex-start',
      }}
    >
      <Box width="100%" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ color: 'black' }}>
          e-Transport
        </Typography>
        <Tooltip title={t('etransport.filterbar.info')} arrow>
          <InfoIcon sx={{ fontSize: '1.5rem', color: 'text.secondary', cursor: 'help' }} />
        </Tooltip>
      </Box>
      <Box width="100%">
        <TargetSearch searchTarget={searchTarget} onSearchChange={onSearchChange} />
      </Box>

      <Box>
        <TimeFilter isLoading={isLoading} activeTab={activeTab} categories={categories} setActiveTab={setActiveTab} />
      </Box>
    </Box>
  );
}

function TargetSearch({
  searchTarget,
  onSearchChange,
}: {
  searchTarget: string | undefined;
  onSearchChange: (value: string | undefined) => void;
}) {
  const { t } = useTranslation();
  return (
    <SearchComponent
      searchText={searchTarget}
      setSearchText={onSearchChange}
      placeholder={t('etransport.filterbar.target.platesearch')}
      showHistory={true}
    />
  );
}

function TimeFilter({
  isLoading,
  activeTab,
  categories,
  setActiveTab,
}: {
  isLoading: boolean;
  activeTab: TimeFilterTabs;
  categories: Map<TimeFilterTabs, number>;
  setActiveTab: (tab: TimeFilterTabs) => void;
}) {
  const { t, i18n } = useTranslation();
  const badgeColors = new Map<TimeFilterTabs, string>([
    ['All', '#333333'],
    ['Inactive', '#bdbdbd'],
    ['Active', 'var(--mui-palette-secondary-main)'],
    ['Future', 'var(--mui-palette-primary-main)'],
  ]);

  function getLabel(label: TimeFilterTabs): string {
    return t(`etransport.filterbar.freight.${label.toLowerCase()}`);
  }

  const minWidthByLocale: Record<string, string> = {
    ro: '210px',
    en: '175px',
    hu: '175px',
  };

  return (
    <CategoryCountSelector
      options={TIME_FILTER_TABS}
      minWidth={minWidthByLocale[i18n.language] ?? '175px'}
      isLoading={isLoading}
      activeTab={activeTab}
      badgeColors={badgeColors}
      numOfCategories={categories}
      getLabel={getLabel}
      setActiveTab={setActiveTab}
    />
  );
}
